import {PickUpNativePermissions} from "../components/PickUpPhotoNativeBridge";


export interface GeoPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

class GeolocationService {
  private currentPosition: GeoPosition | null = null;
  private watchId: number | null = null;
  private isWatching: boolean = false;

  /**
   * 检查位置权限状态
   * @returns Promise<boolean> 返回是否有位置权限
   */
  async checkLocationPermission(): Promise<boolean> {
    try {
      // 优先使用Android应用接口请求权限
      if (typeof (window as any).AndroidApp !== 'undefined') {
        return new Promise((resolve) => {
          const callbackName = `locationPermissionCallback_${Date.now()}`;
          (window as any)[callbackName] = (granted: boolean) => {
            delete (window as any)[callbackName];
            resolve(granted);
          };
          (window as any).AndroidApp.requestLocationPermission(callbackName);
        });
      }
      
      // 如果不是Android应用环境，尝试通过原生桥接请求权限
      await PickUpNativePermissions.request('location');
      return true;
    } catch (error) {
      console.error('Error requesting location permission:', error);
      return false;
    }
  }

  /**
   * 获取单次位置信息，不启动持续监听
   * @returns Promise<GeoPosition>
   */
  async getOneTimePosition(): Promise<GeoPosition> {
    // 如果已经在监听并且有当前位置，直接返回当前位置
    if (this.isWatching && this.currentPosition) {
      return this.currentPosition;
    }

    // 首先检查位置权限
    const hasPermission = await this.checkLocationPermission();
    if (!hasPermission) {
      throw new Error('Location permission is required');
    }

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      // 获取当前位置
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.updatePosition(position);
          resolve(this.currentPosition!);
        },
        (error) => {
          console.error('Error getting current position:', error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          // timeout: 27000
          timeout: 2000
        }
      );
    });
  }

  /**
   * 获取「当前时刻」实时位置：强制不使用缓存（maximumAge:0），用于揽收失败等动作时刻取位。
   * 与 getOneTimePosition 的区别：不复用监听缓存、不吃 30s 缓存，尽量拿到点击那一刻的定位。
   * @returns Promise<GeoPosition>
   */
  async getFreshPosition(): Promise<GeoPosition> {
    const hasPermission = await this.checkLocationPermission();
    if (!hasPermission) {
      throw new Error('Location permission is required');
    }

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.updatePosition(position);
          resolve(this.currentPosition!);
        },
        (error) => {
          console.error('Error getting fresh position:', error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000
        }
      );
    });
  }

  /**
   * 更新位置信息
   * @param position GeolocationPosition
   */
  private updatePosition(position: GeolocationPosition): void {
    this.currentPosition = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp
    };
  }
}

// 创建单例
const geolocationService = new GeolocationService();
export default geolocationService; 